/**
 * @description Tree data State Model + Mutations
 * @author Pranav Ramachandran
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    findNode, clearLoadedKeys,
    buildLookup, findNodeByLookup
} from './tree.helper';
import cloneDeep from "lodash.clonedeep";
import { getTreeNodeData } from 'components/medialibrarymain/mediaLibrary.service';
import { NODE_TYPES } from 'components/medialibrarymain/mediaLibrary.constant';
import { toggleLoader } from 'utils/common';
import { prepareKey } from './tree.constant';

export default function useTreeModel(options) {
    const [treeData, settreeData] = useState([]);
    const [loadedKeys, setloadedKeys] = useState([]);
    const [expandedKeys, setexpandedKeys] = useState([]);
    const [checkedKeys, setCheckedKeys] = useState([]);

    const [selectedKeys, setselectedKeys] = useState([]);
    const [pwd, setpwd] = useState(-1);
    const [nodeLookUp, setnodeLookUp] = useState(new Map());
    const { initialNode, useModel, context, cache = false, customProps={} } = options;
    const [loading, setLoading] = useState(false);
    const [treeVersion, setTreeVersion] = useState(0);
    let rootCache, setRootCacheWithKeys, updateModel;

    if (context) {
        rootCache = context.rootCache;
        setRootCacheWithKeys = context.setRootCacheWithKeys;
        updateModel = context.updateModel;
    }
    let cacheInitialized = useRef(false);

    // API Model -> Tree Model converter
    const treeModelBuilder = useCallback((model, parentId = -1) => {
        const treeData = model.map(node => {
            let data = {
                name: node.text,
                key: node.id,
                isLeaf: node.leaf,
                path: node.path,
                basePath: node.basePath,
                type: node.type,
                parentId,
                thumbnail: node.type === NODE_TYPES.IMAGE ? node.thumbnail : '',
                imageProps: node.type === NODE_TYPES.IMAGE ? node.imageWidth + ' x ' + node.imageHeight : '',
                url: node.url
            };
            if (node.children) {
                data.children = treeModelBuilder(node.children, node.id).treeData
            }
            return data;
        });
        return { treeData }
    }, []);

    function setLoaders(isLoading) {
        toggleLoader(isLoading);
        setLoading(isLoading)
    }

    const setRootData = useCallback((res) => {
        let model = treeModelBuilder(res, -1);
        settreeData(model.treeData);
        setTreeVersion((prevValue) => prevValue + 1);
    }, [treeModelBuilder]);

    // Root level Data Loader - called on load.
    useEffect(() => {
        if (useModel) {
            if (cache) {
                let rootCachekey = prepareKey(initialNode, customProps);

                if (rootCache.hasOwnProperty(rootCachekey)) {
                    if (rootCache[rootCachekey] && rootCache[rootCachekey].length && !cacheInitialized.current) {
                        cacheInitialized.current = true;
                        try {
                            setRootData(rootCache[rootCachekey]);
                            setLoaders(false);
                        } 
                        catch(err){
                            setLoaders(false);
                        }
                    }
                } else {
                    setLoaders(true);
                    updateModel(initialNode, customProps);
                }
            } else {
                setLoaders(true);
                getTreeNodeData(initialNode, customProps).then(res => {
                    setRootData(res);
                }).finally(() => {
                    setLoaders(false);
                });
            }
        }
    }, [initialNode, useModel, rootCache, cache, updateModel, setRootData]);

    useEffect(() => {
        setTreeVersion((prevValue) => prevValue + 1);

    }, [rootCache, cache, treeData, expandedKeys,
        loadedKeys, selectedKeys, checkedKeys, nodeLookUp])

    // Rebuild nodeLookUp on treeData Model change
    useEffect(() => {
        setnodeLookUp(buildLookup(treeData));
    }, [treeData]);

    // Expand a folder -> find child by id
    //TODO need to Fix warning can not update component from external function.
    const getNodeData = (id) => {
        const setNodeData = (res, id) => {
            setloadedKeys(prevState => {
                return [
                    ...prevState,
                    id
                ]
            });
            addNewChild(res, id);
        };

        const addNewChild = (newNodes, id) => {
            // add new child nodes to the specified node id
            let newData = treeModelBuilder(newNodes, id).treeData;
            let data = cloneDeep(treeData);
            let dataObtained = findNode(id, data);
            if (!dataObtained) {
                return;
            }
            if (!newData.length) {
                if (dataObtained.children) {
                    delete dataObtained.children;
                }
            } else {
                dataObtained.children = newData;
            }
            settreeData(data);
        };

        if (cache) {
            let rootCachekey = prepareKey(id, customProps);
            if (rootCache.hasOwnProperty(rootCachekey)) {
                if (rootCache[rootCachekey] && rootCache[rootCachekey].length) {
                    // DIRTY FIX FOR ERROR
                    setTimeout(() => {
                        setNodeData(rootCache[rootCachekey], id);
                    });
                }
                return new Promise((resolve, reject) => { });
            }
        }

        toggleLoader(true);
        // DIRTY FIX FOR ERROR
        setTimeout(() => {
            setLoading(true)
        });

        return getTreeNodeData(id, customProps).then(res => {
            setNodeData(res, id)
            if (cache) {
                setRootCacheWithKeys(id, res, customProps);
            }
        }).finally(() => {
            toggleLoader(false);
            setLoading(false)
        });
    };



    // refresh a node. useful for re render a particular node. 
    // externalRefresh means refresh triggered from outside world (not from tree)
    function refreshNode(keyToRefresh) {
        let keysToRemove = [];
        let loadedKeyList = cloneDeep(loadedKeys);
        loadedKeyList = loadedKeyList.filter(key => key != keyToRefresh);
        let nodeToRefresh = findNodeByLookup(nodeLookUp, keyToRefresh);
        if (nodeToRefresh && nodeToRefresh.children) {
            // need to remove all the childs from loaded keys too
            clearLoadedKeys(nodeToRefresh.children, keysToRemove, loadedKeyList);
            loadedKeyList = loadedKeyList.filter((key) => {
                if (keysToRemove.includes(String(key))) {
                    return false;
                }
                return true;
            });
        }
        setloadedKeys(loadedKeyList);
        settreeData(clearChilds(keyToRefresh));
    }

    function clearChilds(id) {
        let newTreeData = cloneDeep(treeData);
        const loop = (data) => {
            data.forEach((item) => {
                if (id == item.key) {
                    delete item.children;
                    item.isLeaf = false;
                } else {
                    if (item.children && item.children.length) {
                        loop(item.children);
                    }
                }
            });
        };
        loop(newTreeData);
        return newTreeData;
    }
    // restructure expanded keys on various actions. considerExpansionState is the trigger from tree 
    // else external
    function setupExpandedKeys(expandedKeyData, info, considerExpansionState = true) {
        let expandedKeysClone = [...expandedKeys];
        let pwd = info;
        if (considerExpansionState && info.node.props.expanded) {
            // if already expanded remove key from expandedList
            expandedKeysClone.splice(expandedKeysClone.indexOf(info.node.props.eventKey), 1);
            setexpandedKeys(expandedKeysClone);
        } else {
            // add it into expanded key
            if (!considerExpansionState) {
                let node;
                do {
                    node = findNodeByLookup(nodeLookUp, pwd);
                    if (!expandedKeysClone.includes(String(pwd))) {
                        expandedKeysClone.push(String(pwd));
                    }
                    pwd = node.parentId;
                } while (pwd != -1);
                setexpandedKeys(expandedKeysClone);
            } else {
                setexpandedKeys((prevState) => {
                    return [
                        ...prevState,
                        info.node.props.eventKey
                    ]
                });
            }
        }
    }
    // restructure selected keys on various actions. considerSelectedState is the trigger from tree 
    // else external
    function setupSelectedKeys(info, considerSelectedState = true) {
        if (considerSelectedState && info.node.props.selected) {
            // if already selected then remove key from selectedList
            let selectedKeysClone = [...selectedKeys];
            selectedKeysClone.splice(selectedKeysClone.indexOf(info.node.props.eventKey), 1);
            setselectedKeys(selectedKeysClone);
        } else {
            // add it into selected key
            setselectedKeys([considerSelectedState ? info.node.props.eventKey : String(info)]);
            if (!considerSelectedState) {
                setpwd(String(info));
            }
        }
    }

    const setupCheckedKeys = useCallback((info, considerCheckedState = true) => {
        if (considerCheckedState && info.node.props.checked) {
            // if already checked then remove key from checkedList

            setCheckedKeys((prevState) => {
                let keys = [...prevState];
                keys.splice(keys.indexOf(info.node.props.eventKey), 1);
                return keys;
            })

        } else {
            // add it into checked key
            if (considerCheckedState) {
                setCheckedKeys((prevState) => {
                    return [
                        ...prevState,
                        info.node.props.eventKey
                    ]
                });
            }
            else {
                setCheckedKeys(info);
            }
        }
    }, []);

    return [
        treeData,
        loadedKeys,
        pwd,
        setpwd,
        refreshNode,
        getNodeData,
        nodeLookUp,
        expandedKeys,
        setupExpandedKeys,
        selectedKeys,
        setupSelectedKeys,
        checkedKeys,
        setupCheckedKeys,
        loading,
        treeVersion
    ]
}